import React, { useState } from 'react';
import { useCourseProjectList } from '@/hooks/queries/project/useList';
import { useCourseProjectCreate, useCourseProjectUpdate, useCourseProjectDelete } from '@/hooks/mutations/project';
import { CourseProject, CourseProjectInput } from '@/types/courseProjects';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, FolderGit2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { stripHtml } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const CourseProjectsPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedProj, setSelectedProj] = useState<CourseProject | null>(null);

  const [formData, setFormData] = useState<CourseProjectInput>({
    courseId: '',
    translations: [
      { locale: 'uz', title: '', description: '' },
      { locale: 'ru', title: '', description: '' },
      { locale: 'en', title: '', description: '' },
    ],
  });

  const { data: projsData, isLoading, refetch } = useCourseProjectList({
    pageNumber: page,
    pageSize: 50,
  });

  const { mutate: createProject, isPending: isCreating } = useCourseProjectCreate();
  const { mutate: updateProject, isPending: isUpdating } = useCourseProjectUpdate();
  const { mutate: deleteProject } = useCourseProjectDelete();

  const handleOpenCreate = () => {
    setSelectedProj(null);
    setFormData({
      courseId: '',
      translations: [
        { locale: 'uz', title: '', description: '' },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (proj: CourseProject) => {
    setSelectedProj(proj);
    setFormData({
      courseId: proj.courseId || '',
      translations: [
        { locale: 'uz', title: proj.title || '', description: stripHtml(proj.description || '') },
        { locale: 'ru', title: '', description: '' },
        { locale: 'en', title: '', description: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'title' | 'description', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedProj) {
      updateProject(
        { id: selectedProj.id, data: { translations: formData.translations } },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Loyiha yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Loyiha yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createProject(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi kurs loyihasi yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Loyiha yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedProj) return;
    deleteProject(selectedProj.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Loyiha o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedProj(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Loyiha o'chirilmadi", variant: 'destructive' }),
    });
  };

  const projsList: CourseProject[] = projsData?.data?.data || projsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kurs Loyihalari (Projects)</h1>
          <p className="text-sm text-slate-500 mt-1">Talabalar kurs davomida tayyorlaydigan amaliy loyihalar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi loyiha
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : projsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <FolderGit2 className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha kurs loyihalari mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Loyiha Nomi</th>
                  <th className="px-4 py-3.5">Tavsif</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {projsList.map((proj) => (
                  <tr key={proj.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-slate-900">{proj.title || '—'}</td>
                    <td className="px-4 py-3 max-w-md text-xs text-slate-500 line-clamp-2">
                      {stripHtml(proj.description || '') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(proj)}
                          className="size-8 text-slate-600 hover:text-primary rounded-lg"
                        >
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedProj(proj);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500 rounded-lg"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={projsData?.meta?.pagination ?? projsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedProj ? 'Loyihani tahrirlash' : 'Yangi kurs loyihasi yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || { title: '', description: '' };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Loyiha nomi ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="E-commerce Marketplace API"
                          className="rounded-xl"
                          required={locale === 'uz'}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Tavsif ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.description || ''}
                          onChange={(v) => handleTranslationChange(locale, 'description', v)}
                          placeholder="Mikroservis arxitekturasi asosida qurilgan to'liq internet do'kon..."
                          minHeight={80}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Loyihani o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu loyiha o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default CourseProjectsPage;
